import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Switch, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import hostSharedService from '../../../shared/services/hostSharedService';

const CATEGORIES = [
    { value: 'cms', label: 'CMS' },
    { value: 'blog', label: '博客' },
    { value: 'forum', label: '论坛' },
    { value: 'shop', label: '商城' },
    { value: 'tool', label: '工具' },
    { value: 'other', label: '其他' },
];

const WebappApps = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [form] = Form.useForm();

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await hostSharedService.getWebappApps();
            if (res.success) {
                setDataSource(res.data || []);
            }
        } catch (e) {
            message.error('加载失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const openCreate = () => {
        setEditingApp(null);
        form.resetFields();
        form.setFieldsValue({ category: 'cms', sort_order: 0, is_active: 1 });
        setModalVisible(true);
    };

    const openEdit = (record) => {
        setEditingApp(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return; // validation error
        }
        setSubmitting(true);
        try {
            if (editingApp) {
                const res = await hostSharedService.updateWebappApp(editingApp.id, values);
                if (res.success) {
                    message.success('更新成功');
                } else {
                    message.error(res.message || '更新失败');
                    return;
                }
            } else {
                const res = await hostSharedService.createWebappApp(values);
                if (res.success) {
                    message.success('创建成功');
                } else {
                    message.error(res.message || '创建失败');
                    return;
                }
            }
            setModalVisible(false);
            loadData();
        } catch {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await hostSharedService.deleteWebappApp(id);
            if (res.success) {
                message.success('删除成功');
                loadData();
            } else {
                message.error(res.message || '删除失败');
            }
        } catch (e) {
            message.error('删除失败');
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await hostSharedService.toggleWebappApp(id);
            if (res.success) {
                message.success(res.message);
                loadData();
            }
        } catch (e) {
            message.error('操作失败');
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        { title: '应用标识', dataIndex: 'appid', width: 120 },
        { title: '名称', dataIndex: 'name', width: 140 },
        { title: '版本', dataIndex: 'version', width: 80 },
        {
            title: '分类', dataIndex: 'category', width: 80,
            render: (v) => {
                const cat = CATEGORIES.find(c => c.value === v);
                return <Tag>{cat?.label || v}</Tag>;
            }
        },
        { title: '安装路径', dataIndex: 'install_path', width: 180, ellipsis: true },
        { title: '下载地址', dataIndex: 'url', width: 200, ellipsis: true },
        { title: '排序', dataIndex: 'sort_order', width: 60 },
        {
            title: '状态', dataIndex: 'is_active', width: 80,
            render: (v, record) => (
                <Switch checked={!!v} size="small" onChange={() => handleToggle(record.id)} />
            ),
        },
        {
            title: '操作', width: 120, fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Web应用商店"
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>添加应用</Button>
                </Space>
            }
        >
            <Table
                loading={loading}
                dataSource={dataSource}
                columns={columns}
                rowKey="id"
                size="small"
                scroll={{ x: 1200 }}
                locale={{
                    emptyText: loading ? ' ' : (
                        <Empty description="暂无应用，点击右上角“添加应用”创建" />
                    ),
                }}
                pagination={false}
            />

            <Modal
                title={editingApp ? '编辑应用' : '添加应用'}
                open={modalVisible}
                onOk={handleSubmit}
                confirmLoading={submitting}
                onCancel={() => setModalVisible(false)}
                width={640}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="appid" label="应用标识" rules={[{ required: true, message: '请输入应用标识' }]}
                        extra="唯一标识符，如 wordpress、typecho">
                        <Input placeholder="wordpress" disabled={!!editingApp} />
                    </Form.Item>
                    <Form.Item name="name" label="显示名称" rules={[{ required: true, message: '请输入名称' }]}>
                        <Input placeholder="WordPress" />
                    </Form.Item>
                    <Form.Item name="version" label="版本">
                        <Input placeholder="6.7.2" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea rows={2} placeholder="全球流行的博客和内容管理系统" />
                    </Form.Item>
                    <Form.Item name="url" label="安装包下载地址" rules={[{ required: true, message: '请输入下载地址' }]}
                        extra="EP节点将从此地址下载安装包">
                        <Input placeholder="http://..." />
                    </Form.Item>
                    <Form.Item name="md5" label="MD5校验">
                        <Input placeholder="留空则不校验" />
                    </Form.Item>
                    <Form.Item name="appdir" label="应用目录名" extra="安装包解压后的目录名">
                        <Input placeholder="wordpress" />
                    </Form.Item>
                    <Form.Item name="install_path" label="安装入口路径" extra="安装完成后的初始化URL路径">
                        <Input placeholder="wp-admin/install.php" />
                    </Form.Item>
                    <Form.Item name="icon" label="图标URL">
                        <Input placeholder="https://..." />
                    </Form.Item>
                    <Form.Item name="category" label="分类">
                        <Select options={CATEGORIES} />
                    </Form.Item>
                    <Form.Item name="sort_order" label="排序">
                        <InputNumber min={0} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default WebappApps;
